import { DateOnly, format } from '@if/core/date'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridFooter, GridFooterContainer } from '@mui/x-data-grid'
import { BanishmentFormDialog } from '@src/components'
import FullScreenContainer from '@src/components/FullScreenContainer'
import {
  BanishmentFilterDto,
  useCreateBanishment,
  useSearchBanishments,
  useUnbanApplicant,
  useUpdateExpirationDate
} from '@src/data/api/banisment/banishment'
import { Ban, BanishmentFilter, BanishmentForm } from '@src/data/types'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BanishmentSearchFilters from './BanishmentSearchFilters'
import UnbanConfirmationDialog from './UnbanConfirmationDialog'
import UpdateExpirationDateDialog from './UpdateExpirationDateDialog'

const DEFAULT_ROWS_PER_PAGE = 25

const BanishmentsPage = () => {
  const { t } = useTranslation()

  const [queryParams, setQueryParams] = useState<BanishmentFilterDto>({
    offset: 0,
    limit: DEFAULT_ROWS_PER_PAGE,
  })

  const [selectedBan, setSelectedBan] = useState<Ban | null>(null)
  const [unbanDialogOpen, setUnbanDialogOpen] = useState(false)
  const [expirationDateDialogOpen, setExpirationDateDialogOpen] = useState(false)
  const [newBanishmentDialogOpen, setNewBanishmentDialogOpen] = useState(false)

  const [unbanApplicant] = useUnbanApplicant(queryParams)
  const [updateBanExpirationDate] = useUpdateExpirationDate(queryParams)
  const [createBanishment, isCreatingBanishment] = useCreateBanishment(queryParams)

  const handleUnban = (ban: Ban) => {
    setSelectedBan(ban)
    setUnbanDialogOpen(true)
  }

  const handleUpdateExpirationDate = (ban: Ban) => {
    setSelectedBan(ban)
    setExpirationDateDialogOpen(true)
  }

  const confirmUnban = async (banishmentId: string) => {
    try {
      await unbanApplicant(banishmentId)
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const confirmUpdateExpirationDate = async (newExpirationDate: DateOnly | null) => {
    if (!selectedBan) return
    try {
      await updateBanExpirationDate({ banishmentId: selectedBan.id, newExpirationDate: { newExpirationDate } })
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleNewBanishment = () => {
    setNewBanishmentDialogOpen(true)
  }

  const confirmCreateBanishment = async (formData: BanishmentForm) => {
    const banishment = {
      reason: formData.reason,
      expirationDate: formData.expirationDate,
      firstName: formData.firstName,
      lastName: formData.lastName,
      birthDate: formData.birthDate,
      sin: formData.sin,
    }

    try {
      await createBanishment(banishment)
      setNewBanishmentDialogOpen(false)
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const closeDialogs = () => {
    setUnbanDialogOpen(false)
    setExpirationDateDialogOpen(false)
    setNewBanishmentDialogOpen(false)
    setSelectedBan(null)
  }

  const [banishments, isLoading] = useSearchBanishments(queryParams)

  const columns = useMemo<GridColDef<Ban>[]>(
    () => [
      {
        field: 'firstName',
        headerName: t('banishments.columns.name'),
        flex: 1,
        sortable: false,
        renderCell: (params) => `${params.row.firstName} ${params.row.lastName}`,
      },
      {
        field: 'birthDate',
        headerName: t('banishments.columns.birthDate'),
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          return params.row.birthDate ? params.row.birthDate.toString() : ''
        },
      },
      {
        field: 'sin',
        headerName: t('banishments.columns.sin'),
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'reason',
        headerName: t('banishments.columns.reason'),
        flex: 1,
        sortable: false,
      },
      {
        field: 'expirationDate',
        headerName: t('banishments.columns.expirationDate'),
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          return params.row.expirationDate
            ? format(new Date(params.row.expirationDate), 'yyyy-MM-dd')
            : t('banishments.noExpiration')
        },
      },
      {
        field: 'sourceSystem',
        headerName: t('banishments.columns.sourceSystem'),
        minWidth: 150,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: t('banishments.columns.actions'),
        width: 120,
        sortable: false,
        renderCell: (params) => {
          const isEditable = !params.row.sourceSystem

          if (!isEditable) {
            return <Typography variant="caption">{t('banishments.readOnly')}</Typography>
          }

          return (
            <Box>
              <Tooltip title={t('banishments.actions.updateExpirationDate')}>
                <IconButton size="small" onClick={() => handleUpdateExpirationDate(params.row)} color="primary">
                  <EditCalendarIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('banishments.actions.unban')}>
                <IconButton size="small" onClick={() => handleUnban(params.row)} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        },
      },
    ],
    [t],
  )

  const handleFilterSubmit = (filterValues: BanishmentFilter) => {
    setQueryParams({
      firstName: filterValues.firstName || undefined,
      lastName: filterValues.lastName || undefined,
      sin: filterValues.sin || undefined,
      sourceSystem: filterValues.sourceSystem || undefined,
      birthDate:
        filterValues.birthDate instanceof Date
          ? filterValues.birthDate.toISOString()
          : (filterValues.birthDate ?? undefined),
      offset: 0,
      limit: queryParams.limit || DEFAULT_ROWS_PER_PAGE,
    })
  }

  const handleFilterClear = () => {
    setQueryParams({
      offset: 0,
      limit: queryParams.limit || DEFAULT_ROWS_PER_PAGE,
    })
  }

  const paginationModel = useMemo(
    () => ({
      pageSize: queryParams.limit || DEFAULT_ROWS_PER_PAGE,
      page: Math.floor((queryParams.offset || 0) / (queryParams.limit || DEFAULT_ROWS_PER_PAGE)),
    }),
    [queryParams.limit, queryParams.offset],
  )

  const isRowSelectable = () => false

  const handlePageChange = (direction: 'prev' | 'next') => {
    const newOffset =
      direction === 'prev'
        ? Math.max(0, (queryParams.offset || 0) - (queryParams.limit || DEFAULT_ROWS_PER_PAGE))
        : (queryParams.offset || 0) + (queryParams.limit || DEFAULT_ROWS_PER_PAGE)

    setQueryParams((prev) => ({
      ...prev,
      offset: newOffset,
    }))
  }

  const CustomFooter = () => {
    const isPreviousDisabled = (queryParams.offset || 0) === 0
    const isNextDisabled = banishments.length < (queryParams.limit || DEFAULT_ROWS_PER_PAGE)

    return (
      <GridFooterContainer>
        <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
          <Button onClick={() => handlePageChange('prev')} disabled={isPreviousDisabled} sx={{ mx: 1 }}>
            {t('common.previous')}
          </Button>
          <Button onClick={() => handlePageChange('next')} disabled={isNextDisabled} sx={{ mx: 1 }}>
            {t('common.next')}
          </Button>
        </Box>
      </GridFooterContainer>
    )
  }

  return (
    <FullScreenContainer>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1">
          {t('banishments.title')}
        </Typography>
      </Box>

      <BanishmentSearchFilters onSubmit={handleFilterSubmit} onClear={handleFilterClear} />

      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewBanishment} color="error">
            {t('ban.banButtonLabel')}
          </Button>
        </Box>
        <Box sx={{ height: '45rem', width: '100%' }}>
          <DataGrid
            key={`banishments-grid-${queryParams.offset}-${queryParams.limit}`}
            disableColumnFilter
            disableVirtualization={true}
            isRowSelectable={isRowSelectable}
            rows={banishments}
            columns={columns}
            getRowId={(row: Ban) => row.id || row.sin || `${row.firstName}-${row.lastName}-${row.birthDate.toString()}`}
            paginationModel={paginationModel}
            onPaginationModelChange={({ pageSize }) => {
              if (pageSize !== queryParams.limit) {
                setQueryParams((prev) => ({
                  ...prev,
                  limit: pageSize,
                  offset: 0,
                }))
              }
            }}
            loading={isLoading}
            paginationMode="server"
            rowCount={banishments.length}
            slots={{
              footer: CustomFooter,
            }}
            slotProps={{
              pagination: {
                labelRowsPerPage: t('common.customDisplayedRowsTextFooter.RowsPerPage'),
                labelDisplayedRows: () => {
                  const from = banishments.length === 0 ? 0 : (queryParams.offset || 0) + 1
                  const to = Math.min(banishments.length, (queryParams.offset || 0) + banishments.length)

                  return t('common.customDisplayedRowsTextFooter.displayRange', {
                    defaultValue: '',
                    from,
                    to,
                  })
                },
              },
            }}
          />
        </Box>
      </Box>

      <UnbanConfirmationDialog
        open={unbanDialogOpen}
        ban={selectedBan}
        onClose={closeDialogs}
        onConfirm={confirmUnban}
      />

      <UpdateExpirationDateDialog
        open={expirationDateDialogOpen}
        expirationDate={selectedBan?.expirationDate || null}
        onClose={closeDialogs}
        onUpdate={confirmUpdateExpirationDate}
      />

      <BanishmentFormDialog
        open={newBanishmentDialogOpen}
        onConfirm={confirmCreateBanishment}
        onCancel={closeDialogs}
        isLoading={isCreatingBanishment}
      />
    </FullScreenContainer>
  )
}

export default BanishmentsPage
